import { useModal } from 'components/modals/useModal';
import useAuth from 'hooks/useAuth.ts';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useDispatch } from 'store';
import {
    APIInvoiceDetailsSlice,
    useAddNewExpensesMutation,
    useAddNewTimecardsMutation,
} from 'store/api/invoice-details';
import { disableButtons } from 'store/reducers/buttons';
import { LimitType } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { setLocalStorage } from 'utils/localStorage';
import { DetailsTabSuccessMessages } from '../InvoicePage';

type Params = {
    setAddedInvoiceTimecardIds: Dispatch<SetStateAction<number[]>>;
    setAddedInvoiceExpenseIds: Dispatch<SetStateAction<number[]>>;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
};

export type AddNewInvoiceItems = {
    handleAddNewTimecards: (limit: LimitType, invoice: InvoiceData) => Promise<void>;
    handleAddNewExpenses: (limit: LimitType, invoice: InvoiceData) => Promise<void>;
    isAddingNewTimecards: boolean;
    isAddingNewExpenses: boolean;
};

export const useAddNewInvoiceItems = ({
    setAddedInvoiceTimecardIds,
    setAddedInvoiceExpenseIds,
    setSuccessMessages,
}: Params): AddNewInvoiceItems => {
    const { closeModal } = useModal();
    const dispatch = useDispatch();
    const { user } = useAuth();
    const userId = user?.id;

    const [addNewTimecards, { isLoading: isAddingNewTimecards }] = useAddNewTimecardsMutation();
    const [addNewExpenses, { isLoading: isAddingNewExpenses }] = useAddNewExpensesMutation();

    const refetchInvoiceMatterSummaries = useCallback(
        (invoice: InvoiceData) =>
            Promise.all(
                invoice.invoiceMatters.map((im) =>
                    dispatch(
                        APIInvoiceDetailsSlice.endpoints.getInvoiceMatterSummary.initiate(
                            { invoiceId: invoice.id, matterId: im.matterId },
                            { forceRefetch: true }
                        )
                    ).unwrap()
                )
            ),
        [dispatch]
    );

    const handleAddNewTimecards = useCallback(
        async (limit: LimitType, invoice: InvoiceData) => {
            if (!invoice.id) {
                return;
            }

            try {
                const resp = await addNewTimecards({ invoiceId: invoice.id, limit }).unwrap();
                // The base query re-enables buttons once the mutation settles, but the new rows
                // only render after the awaited summaries refetch — keep the guard (and the
                // recalculating overlay) up until then.
                dispatch(disableButtons(true));
                // Await refetch before marking rows as "new" so form state is synced with the
                // refreshed summary — otherwise useWatch returns undefined for new indices.
                await refetchInvoiceMatterSummaries(invoice);
                setLocalStorage(
                    `${userId}-${invoice.id}-timecards-added`,
                    JSON.stringify({
                        limit,
                        timestamp: new Date().getTime(),
                    })
                );
                setAddedInvoiceTimecardIds(resp);
                setSuccessMessages((prev) => ({ ...prev, newTimecardsAdded: true }));
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(disableButtons(false));
                closeModal();
            }
        },
        [
            addNewTimecards,
            closeModal,
            dispatch,
            refetchInvoiceMatterSummaries,
            setAddedInvoiceTimecardIds,
            setSuccessMessages,
            userId,
        ]
    );

    const handleAddNewExpenses = useCallback(
        async (limit: LimitType, invoice: InvoiceData) => {
            if (!invoice.id) {
                return;
            }

            try {
                const resp = await addNewExpenses({ invoiceId: invoice.id, limit }).unwrap();
                dispatch(disableButtons(true));
                await refetchInvoiceMatterSummaries(invoice);
                setLocalStorage(
                    `${userId}-${invoice.id}-expenses-added`,
                    JSON.stringify({
                        limit,
                        timestamp: new Date().getTime(),
                    })
                );
                setAddedInvoiceExpenseIds(resp);
                setSuccessMessages((prev) => ({ ...prev, newExpensesAdded: true }));
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(disableButtons(false));
                closeModal();
            }
        },
        [
            addNewExpenses,
            closeModal,
            dispatch,
            refetchInvoiceMatterSummaries,
            setAddedInvoiceExpenseIds,
            setSuccessMessages,
            userId,
        ]
    );

    return useMemo(
        () => ({ handleAddNewTimecards, handleAddNewExpenses, isAddingNewTimecards, isAddingNewExpenses }),
        [handleAddNewTimecards, handleAddNewExpenses, isAddingNewTimecards, isAddingNewExpenses]
    );
};
